import { httpClient, toClientApiError } from "./httpClient";

export type PermissionCatalogItem = {
  key: string;
  module: string;
  description: string;
  labelHe: string;
  moduleLabelHe: string;
};

export type TenantRole = {
  id: string;
  key: string;
  name: string;
  isSystem: boolean;
  policyVersion: number;
  permissions: Array<{
    permissionKey: string;
    effect: "grant" | "deny";
    scopeMode: "all" | "org_unit";
  }>;
};

export type MembershipPermissionRow = {
  key: string;
  module: string;
  description: string;
  labelHe: string;
  moduleLabelHe: string;
  roleEffect: "grant" | "deny" | null;
  roleScopeMode: "all" | "org_unit" | null;
  overrideEffect: "grant" | "deny" | null;
  effective: {
    allowed: boolean;
    reasonCode: string;
    dataScope: unknown;
  };
};

export type MembershipPermissions = {
  membershipId: string;
  membershipStatus: string;
  userId: string;
  email: string;
  displayName: string;
  roleKeys: string[];
  roleNames: string[];
  policyVersion: number;
  platformRoles: string[];
  permissions: MembershipPermissionRow[];
};

async function wrap<T>(fn: () => Promise<T>): Promise<T> {
  try {
    return await fn();
  } catch (error) {
    throw toClientApiError(error);
  }
}

export const settingsPermissionsApi = {
  listCatalog(): Promise<PermissionCatalogItem[]> {
    return wrap(async () => {
      const res = await httpClient.get<{ items: PermissionCatalogItem[] }>(
        "/settings/permissions/catalog",
      );
      return res.data.items;
    });
  },

  listRoles(): Promise<TenantRole[]> {
    return wrap(async () => {
      const res = await httpClient.get<{ items: TenantRole[] }>("/settings/roles");
      return res.data.items;
    });
  },

  getMembershipPermissions(membershipId: string): Promise<MembershipPermissions> {
    return wrap(async () => {
      const res = await httpClient.get<{ item: MembershipPermissions }>(
        `/settings/memberships/${membershipId}/permissions`,
      );
      return res.data.item;
    });
  },

  setOverride(
    membershipId: string,
    permissionKey: string,
    effect: "grant" | "deny",
  ): Promise<void> {
    return wrap(async () => {
      await httpClient.put(
        `/settings/memberships/${membershipId}/overrides/${encodeURIComponent(permissionKey)}`,
        { effect },
      );
    });
  },

  clearOverride(membershipId: string, permissionKey: string): Promise<void> {
    return wrap(async () => {
      await httpClient.delete(
        `/settings/memberships/${membershipId}/overrides/${encodeURIComponent(permissionKey)}`,
        { data: {} },
      );
    });
  },
};
